import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, inject, input, viewChild } from '@angular/core';
import { popoverAnimation } from '../../../../@vex/animations/popover.animation';
import { ConnectionPositionPair, OverlayModule } from '@angular/cdk/overlay';

export enum PopoverSectionType {
	TEXT = 'text',
	LIST = 'list',
	TABLE = 'table',
}

/**
 * Represents a section within a popover component.
 *
 * A `PopoverSection` is used to define a segment inside a popover, categorizing or grouping
 * related items for better organization and presentation.
 *
 * @interface PopoverSection
 * @property {PopoverSectionType} type - Specifies the type of the section, controlling its visual style or behavior.
 * @property {string[]} items - List of strings representing the individual items contained within this section.
 */
export interface PopoverSection {
	type: PopoverSectionType;
	items?: string[];
	headers?: string[];
	rows?: string[][];
}

/**
 * Represents the content structure for a popover component.
 *
 * This interface defines the primary layout and hierarchies of sections
 * that can be included within a popover. Each popover contains a list of
 * sections that provide the necessary content to display.
 *
 * @interface
 */
export interface PopoverContent {
	sections: PopoverSection[];
}

export type PopoverInput = PopoverContent | string;

/**
 * A component for displaying a popover UI element, which typically appears as a floating box
 * anchored to a trigger element, such as an icon or button. The popover can show contextual information
 * or actions based on the provided content.
 *
 * ### Inputs
 * `content` (PopoverContent | undefined):
 *   The content to be displayed in the popover. It can include multiple sections or be empty/null. It is structured into an Array of `PopoverSection` objects.
 *   Each section can be of a specific type, which determines its visual appearance and behavior.
 */
@Component({
	selector: 'ara-popover',
	imports: [OverlayModule],
	templateUrl: './popover.component.html',
	styleUrl: './popover.component.scss',
	animations: [popoverAnimation],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AraPopoverComponent {
	private readonly cdr = inject(ChangeDetectorRef);

	private readonly triggerEl = viewChild('triggerEl', { read: ElementRef });

	private _content?: PopoverInput;
	private _sections: PopoverSection[] = [];
	protected readonly PopoverSectionType = PopoverSectionType;
	readonly showOnlyIfTruncated = input(false);
	readonly truncationSelector = input<string>(undefined);

	// Delay closing so the pointer can move from the trigger (e.g., info icon) to the popover.
	private closeTimer: ReturnType<typeof setTimeout> | null = null;
	private readonly closeDelayMs = 150;

	isOpen = false;
	private hasBadge = false;
	private readonly positionsAboveFirst: ConnectionPositionPair[] = [
		{
			originX: 'center',
			originY: 'top',
			overlayX: 'center',
			overlayY: 'bottom',
			offsetY: -4,
		},
		{
			originX: 'center',
			originY: 'bottom',
			overlayX: 'center',
			overlayY: 'top',
			offsetY: 4,
		},
	];
	private readonly positionsBelowFirst: ConnectionPositionPair[] = [
		{
			originX: 'center',
			originY: 'bottom',
			overlayX: 'center',
			overlayY: 'top',
			offsetY: 4,
		},
		{
			originX: 'center',
			originY: 'top',
			overlayX: 'center',
			overlayY: 'bottom',
			offsetY: -4,
		},
	];

	get positions(): ConnectionPositionPair[] {
		return this.hasBadge ? this.positionsBelowFirst : this.positionsAboveFirst;
	}

	@Input()
	set content(value: PopoverInput) {
		this._content = value;
		this._sections = this.parseContent(value);
	}

	get content(): PopoverInput | undefined {
		return this._content;
	}

	get sections(): PopoverSection[] {
		return this._sections;
	}

	show() {
		if (this._sections.length === 0) {
			return;
		}
		if (this.showOnlyIfTruncated() && !this.isTriggerContentTruncated()) {
			return;
		}
		this.updateHasBadge();
		this.cancelScheduledClose();
		this.isOpen = true;
		this.cdr.markForCheck();
	}

	hide() {
		this.scheduleClose();
	}

	private parseContent(content?: PopoverInput): PopoverSection[] {
		if (typeof content === 'string') {
			const text = content.trim();
			return text ? [{ type: PopoverSectionType.TEXT, items: [text] }] : [];
		}

		return content?.sections ?? [];
	}

	private updateHasBadge(): void {
		const el = this.triggerEl()?.nativeElement;
		if (!el) {
			return;
		}
		const nextHasBadge =
			!!el.querySelector('.mat-badge-content') ||
			el.classList.contains('mat-badge') ||
			!!el.querySelector('[matBadge]') ||
			!!el.querySelector('.permission-badge');
		this.hasBadge = nextHasBadge;
	}

	private scheduleClose(): void {
		this.cancelScheduledClose();
		this.closeTimer = setTimeout(() => {
			this.isOpen = false;
			this.closeTimer = null;
			this.cdr.markForCheck();
		}, this.closeDelayMs);
	}

	private cancelScheduledClose(): void {
		if (this.closeTimer !== null) {
			clearTimeout(this.closeTimer);
			this.closeTimer = null;
		}
	}

	private isTriggerContentTruncated(): boolean {
		const trigger = this.triggerEl()?.nativeElement;
		if (!trigger) {
			return false;
		}
		const truncationSelector = this.truncationSelector();
		const target = truncationSelector
			? (trigger.querySelector(truncationSelector) as HTMLElement | null)
			: ((trigger.firstElementChild as HTMLElement | null) ?? trigger);

		if (!target) {
			return false;
		}
		return target.scrollWidth > target.clientWidth || target.scrollHeight > target.clientHeight;
	}
}
